import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Spinner } from './States';
import { trackLoginStart, trackLoginWallView } from '../lib/tracking';
import { AoAparecer } from '../lib/tracking/react';

const CHAVE_DESTINO = 'dms:destino-apos-login';

/** Guarda para onde voltar. Some ao fechar a aba, que é o tempo de vida certo. */
export function guardarDestino(destino: string) {
  try {
    sessionStorage.setItem(CHAVE_DESTINO, destino);
  } catch {
    // Aba anônima ou armazenamento bloqueado: perde-se o destino, não o login.
  }
}

/**
 * Devolve a pessoa ao endereço que ela tentou abrir antes de entrar.
 *
 * Chamado uma vez na raiz do app: o retorno da Steam cai sempre na home, e é
 * aqui que o destino guardado é resgatado. Sem isto, travar o catálogo
 * quebraria todo link de skin compartilhado — o visitante entraria e ficaria
 * sem saber o que tinha vindo ver.
 */
export function useRetornoAposLogin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!user) return;
    let destino: string | null = null;
    try {
      destino = sessionStorage.getItem(CHAVE_DESTINO);
      if (destino) sessionStorage.removeItem(CHAVE_DESTINO);
    } catch {
      return;
    }
    // Só age no ponto de chegada do login. Em qualquer outra página, mandar a
    // pessoa embora seria sequestrar a navegação dela.
    if (destino && destino !== pathname && (pathname === '/' || pathname === '/cadastro')) {
      navigate(destino, { replace: true });
    }
  }, [user, pathname, navigate]);
}

/**
 * Porta dos desgastes e anúncios: sem conta Steam, não se escolhe a peça.
 *
 * A vitrine (grade de skins, categorias, kits) é aberta; a guarda fica nas
 * páginas de desgaste e de anúncio, na sacola e no resto que é da conta.
 *
 * Não redireciona para /entrar de propósito. Trocar a página perde o endereço
 * que a pessoa tentou abrir — um link de skin mandado no WhatsApp viraria
 * "entre na loja", e depois do login ela cairia na home sem saber o que veio
 * ver. Aqui a URL permanece, então o próprio retorno da Steam devolve a pessoa
 * onde ela estava.
 *
 * A guarda do servidor é que vale de verdade (as rotas de catálogo respondem
 * 401 sem sessão); esta existe para o visitante ver um convite em vez de uma
 * tela de erro.
 */
export function ExigeLogin({ children }: { children: ReactNode }) {
  const { user, loading, loginUrl, dbAvailable, apiOk } = useAuth();
  const { pathname, search } = useLocation();

  // Enquanto a sessão não foi lida, não dá para decidir: mostrar o convite
  // aqui faria a parede piscar na cara de quem já está logado.
  if (loading) return <Spinner label="Carregando…" />;

  if (user) return <>{children}</>;

  // Sem banco não existe conta para criar, e barrar aqui deixaria o site
  // inacessível por uma decisão de infraestrutura — nesse estado a API também
  // não tem sessão para exigir, então o catálogo segue aberto.
  //
  // Só vale quando a API DISSE isso. Antes bastava `!dbAvailable`, e como uma
  // API fora do ar também zera esse campo, a parede sumia junto com o servidor
  // — o bloqueio caía exatamente na hora em que ninguém estava vigiando.
  if (apiOk && !dbAvailable) return <>{children}</>;

  return (
    <div className="login-stage">
      <AoAparecer disparar={trackLoginWallView} />
      <div className="login-wall">
        <div className="login-wall-selo" aria-hidden>
          <span className="login-wall-anel" />
          <svg viewBox="0 0 24 24" width="26" height="26">
            <path
              d="M7 10V7.5a5 5 0 0 1 10 0V10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <rect x="4.5" y="10" width="15" height="10.5" rx="2.5" fill="currentColor" />
            <circle cx="12" cy="15.2" r="1.5" fill="var(--panel)" />
          </svg>
        </div>

        <span className="login-wall-sobretitulo">Área de clientes</span>
        <h2>Entre para ver os desgastes e os anúncios</h2>
        <p>
          O catálogo é aberto. Para escolher o desgaste e a peça que vai comprar, entre com a sua conta: a Steam
          confirma quem é você, e nada além do seu perfil público é lido. Não pedimos senha.
        </p>

        {/* O login da Steam sempre devolve na home: a volta do OpenID não
            carrega destino. Guardar o endereço aqui é o que faz um link de skin
            compartilhado terminar na skin, e não numa home sem contexto. */}
        <a className="login-wall-cta" href={loginUrl} onClick={() => {
            guardarDestino(`${pathname}${search}`);
            trackLoginStart();
          }}
        >
          <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
            <circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" strokeWidth="1.9" />
            <circle cx="15.1" cy="8.9" r="2.9" fill="none" stroke="currentColor" strokeWidth="1.9" />
            <circle cx="8.2" cy="14.6" r="2.2" fill="currentColor" />
            <line x1="9.6" y1="13.4" x2="13.4" y2="10.1" stroke="currentColor" strokeWidth="1.7" />
          </svg>
          Entrar com a Steam
          <span className="login-wall-seta" aria-hidden>
            →
          </span>
        </a>

        <div className="login-wall-motivos">
          <div className="login-wall-motivo">
            <b>Preço final em reais</b>
            <span>Sem cadastro de cartão e sem carteira</span>
          </div>
          <div className="login-wall-motivo">
            <b>Entrega no seu trade link</b>
            <span>Você fecha o pedido e fala com uma pessoa</span>
          </div>
          <div className="login-wall-motivo">
            <b>Upgrade e venda</b>
            <span>Seu inventário vira crédito na mesma conta</span>
          </div>
        </div>

        <p className="login-wall-rodape">
          Login pela Steam, sem senha. O acesso é feito no site oficial da Valve.
        </p>
      </div>
    </div>
  );
}
